import { inject, observer } from "mobx-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Product } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import { getUserSelectedWarehouse, setUserSelectedWarehouse } from '../../utils/LocalStorageUtils';
import { useGetV4WarehousesQuery } from "randmar-api-client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from "randmarcomps";
import InlineSpinner from "../util/InlineSpinner";

interface AddToRequisitionProps {
  store?: RootStore;
  product: Product;
  children: React.ReactNode;
}

interface AddToRequisitionDialogProps {
  store?: RootStore;
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const normalizeWarehouseField = (value?: string | null) => (value || "").trim().toLowerCase();

const AddToRequisitionDialog: React.FC<AddToRequisitionDialogProps> = ({ store, product, open, onOpenChange }) => {
  const { applicationId } = useManufacturer();
  const [loading, setLoading] = useState(false);
  const [chosenWarehouse, setChosenWarehouse] = useState<string>("");
  const initialQty = useMemo(() => product.masterCarton || 1, [product.masterCarton]);
  const [quantityDisplay, setQuantityDisplay] = useState<number>(initialQty);
  const [quantityReal, setQuantityReal] = useState<number>(initialQty);

  const {
    data: warehouses,
    isLoading: warehousesLoading,
    isError: warehousesError,
    isSuccess: warehousesSuccess,
  } = useGetV4WarehousesQuery();

  const activeFulfillmentCenters = useMemo(() => {
    const excludedStatuses = new Set(["inactive", "closing", "closed"]);

    return (
      warehouses?.filter((warehouse) => {
        if (!warehouse.WarehouseId) return false;

        const warehouseType = normalizeWarehouseField(warehouse.WarehouseType);
        if (!warehouseType.includes("fulfillment center")) return false;

        const status = normalizeWarehouseField(warehouse.Status);
        if (excludedStatuses.has(status)) return false;

        return true;
      }) ?? []
    );
  }, [warehouses]);

  const isChosenWarehouseValid = useMemo(() => {
    if (!chosenWarehouse) return false;
    return activeFulfillmentCenters.some((warehouse) => warehouse.WarehouseId === chosenWarehouse);
  }, [activeFulfillmentCenters, chosenWarehouse]);

  useEffect(() => {
    if (!open) return;
    setChosenWarehouse(getUserSelectedWarehouse());
    setQuantityDisplay(initialQty);
    setQuantityReal(initialQty);
  }, [initialQty, open]);

  useEffect(() => {
    if (!open) return;
    if (!warehousesSuccess) return;

    if (activeFulfillmentCenters.length === 0) {
      if (chosenWarehouse) setChosenWarehouse("");
      return;
    }

    if (isChosenWarehouseValid) return;

    const storedWarehouseId = getUserSelectedWarehouse();
    const storedWarehouseIsValid = activeFulfillmentCenters.some((warehouse) => warehouse.WarehouseId === storedWarehouseId);
    if (storedWarehouseIsValid) {
      setChosenWarehouse(storedWarehouseId);
      return;
    }

    const fallbackWarehouseId = activeFulfillmentCenters[0]?.WarehouseId || "";
    if (!fallbackWarehouseId) {
      if (chosenWarehouse) setChosenWarehouse("");
      return;
    }

    setChosenWarehouse(fallbackWarehouseId);
    setUserSelectedWarehouse(fallbackWarehouseId);
  }, [activeFulfillmentCenters, chosenWarehouse, isChosenWarehouseValid, open, warehousesSuccess]);

  const handleCancel = useCallback(() => {
    if (loading) return;
    onOpenChange(false);
  }, [loading, onOpenChange]);

  const propertyChange = useCallback((propertyName: string, newValue: string) => {
    switch (propertyName) {
      case "warehouse":
        setChosenWarehouse(newValue);
        setUserSelectedWarehouse(newValue);
        break;
      case "quantity": {
        const mc = product.masterCarton;
        if (isNaN(parseInt(newValue)) || parseInt(newValue) < 0) newValue = (mc || 0).toString();
        const numericValue = parseInt(newValue);
        if (mc !== null && mc !== undefined && mc > 0) {
          setQuantityDisplay(numericValue);
          setQuantityReal(Math.floor(numericValue / mc) * mc);
        } else {
          setQuantityDisplay(numericValue);
          setQuantityReal(numericValue);
        }
        break;
      }
    }
  }, [product.masterCarton]);

  const handleConfirmRequisitionRequest = useCallback(() => {
    setLoading(true);
    const { productStore, snackStore, documentsStore } = store!;
    productStore
      .addToRequisition(applicationId, product.randmarSKU || "", chosenWarehouse, quantityReal.toString())
      .then((requisitionNumber) => {
        if (requisitionNumber) {
          snackStore.showSuccess(`Requestition successfully made.`);
          if (documentsStore.requisitions.length === 0) documentsStore.getRequisitions(applicationId);
        } else snackStore.showError(`Could not request product`);

        productStore.getProduct(applicationId, product.randmarSKU || "");
        setLoading(false);
        onOpenChange(false);
      });
  }, [applicationId, chosenWarehouse, onOpenChange, product.randmarSKU, quantityReal, store]);

  return (
    <Dialog
      modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (loading) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="sm:max-w-lg z-[10001]"
        overlayClassName="z-[10000]"
        onInteractOutside={(event) => {
          const eventType = event.detail?.originalEvent?.type;
          if (eventType === "focusin") {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Request an order for {product.mpn || product.randmarSKU}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">Master carton quantity: {product.masterCarton}</p>

          <div className="space-y-2">
            <Label>Warehouse</Label>
            <Select
              value={chosenWarehouse}
              onValueChange={(value) => propertyChange("warehouse", value)}
              disabled={loading || warehousesLoading || warehousesError}
            >
              <SelectTrigger className="h-11 rounded-xl">
                <SelectValue placeholder="Select a warehouse" />
              </SelectTrigger>
              <SelectContent className="z-[10002] max-h-72 overflow-y-auto">
                {warehousesLoading ? (
                  <SelectItem value="loading" disabled>
                    Loading warehouses…
                  </SelectItem>
                ) : warehousesError ? (
                  <SelectItem value="error" disabled>
                    Unable to load warehouses
                  </SelectItem>
                ) : warehousesSuccess && activeFulfillmentCenters.length === 0 ? (
                  <SelectItem value="empty" disabled>
                    No active fulfillment centers available
                  </SelectItem>
                ) : (
                  activeFulfillmentCenters.map((warehouse) => (
                    <SelectItem key={`Warehouse-${warehouse.WarehouseId}`} value={warehouse.WarehouseId!}>
                      {warehouse.Name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="requisition-quantity">Quantity</Label>
            <div className="flex items-center gap-3">
              <Input
                id="requisition-quantity"
                type="number"
                inputMode="numeric"
                value={quantityDisplay}
                onChange={(e) => propertyChange("quantity", e.target.value)}
                disabled={loading || warehousesLoading}
              />
              <span className="text-sm text-muted-foreground">units</span>
            </div>
          </div>

          <Separator />

          <div className="text-sm">
            <p>
              Reorder quantity: <span className="font-semibold text-foreground">{quantityReal}</span>
            </p>
            <p className="text-muted-foreground">
              Total master cartons:{" "}
              <span className="font-semibold text-foreground">{quantityReal / (product.masterCarton || 1)}</span>
            </p>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-live="polite">
              <InlineSpinner size="sm" decorative />
              Submitting…
            </div>
          ) : null}
        </div>

        <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirmRequisitionRequest}
            disabled={loading || warehousesLoading || warehousesError || quantityReal <= 0 || !isChosenWarehouseValid}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddToRequisition: React.FC<AddToRequisitionProps> = ({ product, children, store }) => {
  const [open, setOpen] = useState(false);

  const handleAddToRequisitionButton = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <>
      <span className="contents" onClick={handleAddToRequisitionButton}>
        {children}
      </span>

      <AddToRequisitionDialog store={store} product={product} open={open} onOpenChange={setOpen} />
    </>
  );
};

const WrappedAddToRequisitionDialog = inject("store")(observer(AddToRequisitionDialog));

export { WrappedAddToRequisitionDialog as AddToRequisitionDialog };

export default inject("store")(observer(AddToRequisition));
